import { useQuery } from "@tanstack/react-query";
import { unwrap } from "@brad-tipper/app-frontend";
import { useLocalSearchParams } from "expo-router";
import { ActivityIndicator, StyleSheet, View } from "react-native";

import {
  AppText,
  Card,
  EmptyState,
  ListRow,
  PageTitle,
  ScrollScreen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import { api } from "~/api/client";
import { spacing } from "~/styles/theme";

export default function SharedCollectionScreen() {
  const { token } = useLocalSearchParams<{ token: string }>();
  const shareToken = typeof token === "string" ? token : "";

  const collectionQuery = useQuery({
    queryKey: ["shared-collection", shareToken],
    enabled: Boolean(shareToken),
    retry: false,
    queryFn: () => unwrap(api.getSharedCollection({ path: { token: shareToken } })),
  });

  if (!shareToken) {
    return (
      <ScrollScreen>
        <EmptyState
          title="Missing share link"
          description="This share URL is incomplete."
        />
      </ScrollScreen>
    );
  }

  if (collectionQuery.isLoading) {
    return (
      <ScrollScreen>
        <View style={stylesheet.loading}>
          <ActivityIndicator />
        </View>
      </ScrollScreen>
    );
  }

  if (collectionQuery.isError || !collectionQuery.data) {
    return (
      <ScrollScreen>
        <EmptyState
          title="Collection unavailable"
          description="This share link may have expired or the collection is private."
        />
      </ScrollScreen>
    );
  }

  const collection = collectionQuery.data;
  const plants = collection.plants;

  return (
    <ScrollScreen>
      <Stack gap="lg">
        <PageTitle
          title={collection.name}
          subtitle={
            collection.owner_handle
              ? `Shared by @${collection.owner_handle}`
              : "Shared collection"
          }
        />
        {collection.description ? (
          <AppText size="sm">{collection.description}</AppText>
        ) : null}

        <Card gap="md">
          <SectionHeader title="Plants" />
          {plants.length === 0 ? (
            <EmptyState
              title="No plants yet"
              description="This collection does not have any plants to show."
            />
          ) : (
            <Stack gap="xs">
              {plants.map((plant) => (
                <ListRow
                  key={plant.id}
                  title={plant.display_name}
                  description={plant.status}
                />
              ))}
            </Stack>
          )}
        </Card>
      </Stack>
    </ScrollScreen>
  );
}

const stylesheet = StyleSheet.create({
  loading: {
    paddingVertical: spacing.xl,
    alignItems: "center",
  },
});
